import { LayersClear } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { Duration } from 'luxon';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';
import { useDeleteOverlay, useUpdateOverlay } from '~/api/overlays';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { NumberInput } from '~/components/inputs';
import type { PartialContent } from '~/components/media/card';
import { assert } from '~/lib/assert';
import { i18n } from '~/lib/i18n';
import { VIDEO_LIKE_KINDS } from '~/lib/media';

const DURATION_INPUT_ID = 'overlay-settings-duration';

const validationSchema = object({
  duration: number().required(i18n.t('errors.validation.required')),
});

export interface OverlaySettingsDialogProps {
  close: () => void;
  open: boolean;
  item: PartialContent;
}

export const OverlaySettingsDialog = ({ close, open, item }: OverlaySettingsDialogProps) => {
  const { t } = useTranslation();
  const [deleteOverlay] = useDeleteOverlay();
  const [updateOverlay] = useUpdateOverlay();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      duration: Duration.fromISO(item.overlay?.duration ?? 'PT30S').as('seconds'), // shouldn't happen
    },
    onSubmit: async (values) => {
      assert(item.overlay != null, 'content item is not an overlay');
      const newValues = validationSchema.cast(values);
      const { errors } = await updateOverlay({
        variables: {
          overlayId: item.overlay.id,
          patch: { duration: Duration.fromObject({ seconds: newValues.duration }).toISO() },
        },
      });
      if (!errors?.length) close();
    },
    validateOnBlur: true,
    validationSchema,
  });

  const handleClose = useCallback(() => {
    close();
    formik.resetForm();
  }, [formik, close]);

  const removeOverlay = async () => {
    assert(item.overlay != null, 'content item is not an overlay');
    await deleteOverlay({ variables: { input: { overlayId: item.overlay.id } } });
    handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open}>
      <DialogTitle onClose={handleClose}>
        {t('components.overlay_settings_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('components.overlay_settings_dialog.description')}
        </DialogContentTitle>
        <DialogForm id="overlay-settings" onSubmit={formik.handleSubmit}>
          {/* htmlFor, or the wrapping label would name the decrement button, its first control. */}
          <label htmlFor={DURATION_INPUT_ID}>
            <span className={`label required`}>{t('common.labels.duration')}</span>
            <NumberInput
              disabled={
                formik.isSubmitting ||
                (item.__typename === 'MediaItem' && VIDEO_LIKE_KINDS.includes(item.kind))
              }
              id={DURATION_INPUT_ID}
              onBlur={formik.handleBlur}
              onChange={(_, val) => formik.setFieldValue('duration', val)}
              value={formik.values.duration}
              helperText={t('components.overlay_settings_dialog.duration_helper')}
              min={5}
            />
          </label>
          {item.__typename === 'MediaItem' && VIDEO_LIKE_KINDS.includes(item.kind) && (
            <Typography variant="body2" display="block">
              {t('components.overlay_settings_dialog.video_locked')}
            </Typography>
          )}
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={handleClose} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Tooltip title={item.overlay?.canDestroy.reasons?.fullMessages.join(', ') ?? ''}>
          <span>
            <DeleteButton
              disabled={item.overlay?.canDestroy.value === false}
              onClick={removeOverlay}
              startIcon={<LayersClear />}
            >
              {t('components.overlay_settings_dialog.remove')}
            </DeleteButton>
          </span>
        </Tooltip>
        <SaveButton
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          form="overlay-settings"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.save')}
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};
